import React, { type Ref, forwardRef } from 'react';
import Button, {
	IconButton,
	LinkButton,
	LinkIconButton,
	type ButtonProps,
	type LinkButtonProps,
	type IconButtonProps,
} from '@atlaskit/button/new';
import type { IconProps } from '@atlaskit/icon/types';
import { Inline } from '@atlaskit/primitives';
import { Show, type Breakpoint } from '@atlaskit/primitives/responsive';
import Tooltip from '@atlaskit/tooltip';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { RESPONSIVE_BUTTON_DEFAULT_TEST_ID } from '../common/constants';

export type ResponsiveButtonPropsOld =
	// Inherits Atlaskit button props except some exceptions where we are handling something explicitly or not supporting it
	Omit<ButtonProps, 'iconBefore' | 'children' | 'appearance'> & {
		// Atlaskit Icon to be displayed on the button. Format is icon={YourIconName}
		icon: React.ComponentType<IconProps>;
		// Label text the expanded button and tooltip on the collapsed (icon-only) button
		label: string;
		// Media query above which the button will be shown in expanded form. Default is lg.
		breakpoint?: Exclude<Breakpoint, 'xxs'>;
		// Button appearance; supports the values valid for both the 'full' and 'icon' variations
		appearance?: React.ComponentProps<typeof IconButton>['appearance'];
		// Pass a ref (e.g., for popup and dropdown menu trigger buttons)
		ref?: Ref<HTMLButtonElement>;
	};

export type ResponsiveLinkButtonPropsOld = ResponsiveButtonPropsOld &
	Pick<LinkButtonProps, 'href' | 'target'>;

type ResponsiveButtonWrapperPropsOld = (ResponsiveButtonPropsOld | ResponsiveLinkButtonPropsOld) & {
	isLink?: boolean;
};

const ResponsiveButtonWrapper = forwardRef((props: ResponsiveButtonWrapperPropsOld, ref) => {
	const { icon, label, iconAfter, breakpoint, appearance, testId, isLink, ...restProps } = props;

	const CollapsedButtonComponent = isLink ? LinkIconButton : IconButton;
	const ExpandedButtonComponent = isLink ? LinkButton : Button;

	return (
		<Inline ref={ref}>
			<Show below={breakpoint ?? 'lg'}>
				<Tooltip content={label}>
					{/* @ts-expect-error TS2322 Type ResponsiveButtonProps is not assignable to type ResponsiveLinkButtonProps because of href and target */}
					<CollapsedButtonComponent
						{...restProps}
						icon={icon}
						appearance={appearance ?? 'default'}
						label={label}
						testId={`${testId ?? RESPONSIVE_BUTTON_DEFAULT_TEST_ID}.collapsed`}
						{
							/* @ts-expect-error TS2551: Property 'href' does not exist on type ResponsiveButtonProps */
							...(isLink && { href: restProps.href })
						}
					/>
				</Tooltip>
			</Show>
			<Show above={breakpoint ?? 'lg'}>
				{/* @ts-expect-error TS2322 Type ResponsiveButtonProps is not assignable to type ResponsiveLinkButtonProps because of href and target */}
				<ExpandedButtonComponent
					{...restProps}
					iconBefore={icon}
					iconAfter={iconAfter}
					appearance={appearance ?? 'default'}
					testId={`${testId ?? RESPONSIVE_BUTTON_DEFAULT_TEST_ID}.expanded`}
					{
						/* @ts-expect-error TS2551: Property 'href' does not exist on type ResponsiveButtonProps */
						...(isLink && { href: restProps.href })
					}
				>
					{label}
				</ExpandedButtonComponent>
			</Show>
		</Inline>
	);
});

/**
 * Displays an IconButton+tooltip or a Button with iconBefore, depending on viewport size.
 */
const ResponsiveButtonOld = forwardRef((props: ResponsiveButtonPropsOld, ref) => (
	<ResponsiveButtonWrapper ref={ref} {...props} />
));

/**
 * Displays a LinkIconButton+tooltip or a LinkButton with iconBefore, depending on viewport size.
 */
const ResponsiveLinkButtonOld = forwardRef((props: ResponsiveLinkButtonPropsOld, ref) => (
	<ResponsiveButtonWrapper isLink ref={ref} {...props} />
));

export type ResponsiveBaseButtonPropsNew = {
	// Atlaskit Icon to be displayed on the button. Format is icon={YourIconName}
	icon: React.ComponentType<IconProps>;
	// Label text the expanded button and tooltip on the collapsed (icon-only) button
	label: string;
	// Media query above which the button will be shown in expanded form. Default is lg.
	breakpoint?: Exclude<Breakpoint, 'xxs'>;
	// Button appearance; supports the values valid for both the 'full' and 'icon' variations
	appearance?: React.ComponentProps<typeof IconButton>['appearance'];
	// Controls the amount of padding in the icon button. Can be 'compact' | 'default'
	iconButtonSpacing?: IconButtonProps['spacing'];
	// Controls the amount of padding in the button. Can be 'compact' | 'default' | 'none'
	buttonSpacing?: ButtonProps['spacing'];
};

export type ResponsiveLinkButtonPropsNew =
	// Inherits Atlaskit link button props except some exceptions where we are handling something explicitly or not supporting it
	Omit<LinkButtonProps, 'iconBefore' | 'children' | 'appearance' | 'spacing'> &
		ResponsiveBaseButtonPropsNew;

export type ResponsiveButtonPropsNew =
	// Inherits Atlaskit button props except some exceptions where we are handling something explicitly or not supporting it
	Omit<ButtonProps, 'iconBefore' | 'children' | 'appearance' | 'spacing'> &
		ResponsiveBaseButtonPropsNew;

/**
 * Displays an IconButton+tooltip or a Button with iconBefore, depending on viewport size.
 */
export const ResponsiveButtonNew = forwardRef<HTMLButtonElement, ResponsiveButtonPropsNew>(
	(props, ref) => {
		const {
			icon,
			label,
			iconAfter,
			breakpoint,
			appearance,
			testId,
			'aria-label': ariaLabel,
			iconButtonSpacing,
			buttonSpacing,
			...restProps
		} = props;
		return (
			<Inline>
				<Show below={breakpoint ?? 'lg'}>
					<Tooltip content={label}>
						<IconButton
							{...restProps}
							icon={icon}
							appearance={appearance ?? 'default'}
							label={label}
							testId={`${testId ?? RESPONSIVE_BUTTON_DEFAULT_TEST_ID}.collapsed`}
							ref={ref}
							spacing={iconButtonSpacing}
						/>
					</Tooltip>
				</Show>
				<Show above={breakpoint ?? 'lg'}>
					<Button
						{...restProps}
						iconBefore={icon}
						iconAfter={iconAfter}
						appearance={appearance ?? 'default'}
						testId={`${testId ?? RESPONSIVE_BUTTON_DEFAULT_TEST_ID}.expanded`}
						ref={ref}
						aria-label={ariaLabel}
						spacing={buttonSpacing}
					>
						{label}
					</Button>
				</Show>
			</Inline>
		);
	},
);

/**
 * Displays a LinkIconButton+tooltip or a LinkButton with iconBefore, depending on viewport size.
 */
export const ResponsiveLinkButtonNew = forwardRef<HTMLAnchorElement, ResponsiveLinkButtonPropsNew>(
	(props: ResponsiveLinkButtonPropsNew, ref) => {
		const {
			icon,
			label,
			iconAfter,
			breakpoint,
			appearance,
			testId,
			'aria-label': ariaLabel,
			iconButtonSpacing,
			buttonSpacing,
			...restProps
		} = props;

		return (
			<Inline>
				<Show below={breakpoint ?? 'lg'}>
					<Tooltip content={label}>
						<LinkIconButton
							{...restProps}
							icon={icon}
							appearance={appearance ?? 'default'}
							label={label}
							testId={`${testId ?? RESPONSIVE_BUTTON_DEFAULT_TEST_ID}.collapsed`}
							href={restProps.href}
							ref={ref}
							spacing={iconButtonSpacing}
						/>
					</Tooltip>
				</Show>
				<Show above={breakpoint ?? 'lg'}>
					<LinkButton
						{...restProps}
						iconBefore={icon}
						iconAfter={iconAfter}
						appearance={appearance ?? 'default'}
						testId={`${testId ?? RESPONSIVE_BUTTON_DEFAULT_TEST_ID}.expanded`}
						href={restProps.href}
						ref={ref}
						aria-label={ariaLabel}
						spacing={buttonSpacing}
					>
						{label}
					</LinkButton>
				</Show>
			</Inline>
		);
	},
);

/**
 * Displays an IconButton+tooltip or a Button with iconBefore, depending on viewport size.
 */
export const ResponsiveButton = componentWithFF(
	'refactor-jira-responsive-button_3ifhq',
	ResponsiveButtonNew,
	ResponsiveButtonOld,
);

/**
 * Displays a LinkIconButton+tooltip or a LinkButton with iconBefore, depending on viewport size.
 */
export const ResponsiveLinkButton = componentWithFF(
	'refactor-jira-responsive-button_3ifhq',
	ResponsiveLinkButtonNew,
	ResponsiveLinkButtonOld,
);
